import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  Divider,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ExpandMore,
  NotificationsActive,
  Group,
  Forum,
  Chat,
  Save,
} from '@mui/icons-material';

const NotificationToggle = ({ title, description, checked, onChange, disabled }) => {
  const theme = useTheme();
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      py: 1.5,
      opacity: disabled ? 0.5 : 1
    }}>
      <Box sx={{ pr: 2 }}>
        <Typography variant="body1" fontWeight={600}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
      </Box>
      <Switch
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        color="primary"
      />
    </Box>
  );
};

export function NotificationSettings() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [preferences, setPreferences] = useState({
    enableNotifications: true,
    mentions: true,
    passwordChange: true,
    activityReply: true,
    groupUpdates: true,
    groupPromotion: true,
    groupInvites: true,
  });

  const handleToggle = (key) => {
    setPreferences((prevState) => {
      const newState = { ...prevState, [key]: !prevState[key] };
      if (key === 'enableNotifications' && !newState.enableNotifications) {
        Object.keys(newState).forEach((pref) => {
          if (pref !== 'enableNotifications') newState[pref] = false;
        });
      }
      return newState;
    });
  };

  const renderPreferences = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Choose which notifications you want to receive and where.
      </Typography>

      <Paper 
        elevation={0} 
        sx={{ 
          p: 2.5, 
          mb: 4, 
          borderRadius: '16px', 
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.2),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <NotificationsActive color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>Master Notification Switch</Typography>
        </Box>
        <Switch
          checked={preferences.enableNotifications}
          onChange={() => handleToggle('enableNotifications')}
          color="primary"
        />
      </Paper>

      <Stack spacing={3}>
        <Box>
          <Typography variant="overline" fontWeight={800} color="primary" gutterBottom sx={{ letterSpacing: '1px' }}>MENTIONS</Typography>
          <NotificationToggle
            title="Profile Mentions"
            description='When someone mentions you with @username'
            checked={preferences.mentions}
            onChange={() => handleToggle('mentions')}
            disabled={!preferences.enableNotifications}
          />
        </Box>

        <Divider />

        <Box>
          <Typography variant="overline" fontWeight={800} color="primary" gutterBottom sx={{ letterSpacing: '1px' }}>ACCOUNT ACTIVITY</Typography>
          <NotificationToggle
            title="Security Alerts"
            description="Notifications for password changes and security updates"
            checked={preferences.passwordChange}
            onChange={() => handleToggle('passwordChange')}
            disabled={!preferences.enableNotifications}
          />
        </Box>

        <Divider />

        <Box>
          <Typography variant="overline" fontWeight={800} color="primary" gutterBottom sx={{ letterSpacing: '1px' }}>SOCIAL GROUPS</Typography>
          <NotificationToggle
            title="Group Updates"
            description="When a group you manage is updated"
            checked={preferences.groupUpdates}
            onChange={() => handleToggle('groupUpdates')}
            disabled={!preferences.enableNotifications}
          />
          <NotificationToggle
            title="Promotions"
            description="When you receive a promotion within a group"
            checked={preferences.groupPromotion}
            onChange={() => handleToggle('groupPromotion')}
            disabled={!preferences.enableNotifications}
          />
          <NotificationToggle
            title="Invitations"
            description="When you receive a new group invite"
            checked={preferences.groupInvites}
            onChange={() => handleToggle('groupInvites')}
            disabled={!preferences.enableNotifications}
          />
        </Box>
      </Stack>

      <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
        <Button 
          variant="contained" 
          size="large" 
          startIcon={<Save />}
          sx={{ px: 8, py: 1.5, borderRadius: '50px', fontWeight: 800 }}
        >
          Save Preferences
        </Button>
      </Box>
    </Box>
  );

  const renderSubscriptions = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Manage your active forum and group subscriptions.
      </Typography>
      
      {[
        { label: 'Groups', icon: <Group /> },
        { label: 'Forums', icon: <Forum /> },
        { label: 'Discussions', icon: <Chat /> }
      ].map((cat, idx) => (
        <Accordion key={idx} sx={{ 
          mb: 2, 
          borderRadius: '16px !important', 
          '&:before': { display: 'none' },
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none'
        }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" spacing={2} alignItems="center">
              {cat.icon}
              <Typography fontWeight={700}>{cat.label}</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              You are not currently subscribed to any {cat.label.toLowerCase()}.
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} gutterBottom>Notification Settings</Typography>
      
      <Tabs 
        value={activeTab} 
        onChange={(e, v) => setActiveTab(v)}
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          '& .MuiTab-root': { fontWeight: 700, minWidth: 150 }
        }}
      >
        <Tab label="Preferences" />
        <Tab label="Subscriptions" />
      </Tabs>

      {activeTab === 0 ? renderPreferences() : renderSubscriptions()}
    </Box>
  );
}
